import { Contract } from '@ekt-group/general-purpose-api-interfaces';
import { ArrowLeft, Paperclip, Grid01 } from '@untitled-ui/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import logo from '@/assets/logo.png';
import { useUserInfoStore } from '@/store';
import { IHelpdeskTicket } from '@/types/helpdesk';
import { cn } from '@/utils/cn';

import { useGetContracts } from '../../contracts/api/use-get-contracts';
import { useCreateHelpdeskTicket } from '../api/use-create-helpdesk-ticket';
import { useGetHelpdeskTicketTypes } from '../api/use-get-helpdesk-ticket-types';
import { useGetHelpdeskTickets } from '../api/use-get-helpdesk-tickets';

type Props = {
  onBackButtonClick: () => void;
  openTicketChat: (ticket: IHelpdeskTicket) => void;
};

enum MessageType {
  Request = 'request',
  Response = 'response',
}

interface IMessage {
  type: MessageType;
  content: string;
}

const CreateTicketForm = ({ onBackButtonClick, openTicketChat }: Props) => {
  const { t } = useTranslation('communicationPage');
  const { code: ownerCode, name: accountName } = useUserInfoStore((state) => state.selectedAccount);

  const [isButtonsPanelShown, setIsButtonsPanelShown] = useState(true);
  const [ticketCreationStep, setTicketCreationStep] = useState(1);
  const [selectedContractId, setSelectedContractId] = useState<number>();
  const [selectedTicketTypeId, setSelectedTicketTypeId] = useState<number>();
  const [messages, setMessages] = useState<IMessage[]>([]);

  const { refetch: refetchTickets } = useGetHelpdeskTickets();

  const { mutate: createHelpdeskTicket } = useCreateHelpdeskTicket({
    onSuccess: async ([ticketId]) => {
      const { data } = await refetchTickets();
      const ticket = data?.find((ticket) => ticket.id === ticketId);
      onBackButtonClick();
      openTicketChat(ticket);
    },
    onError: (e) => console.log(e),
  });

  const contractsQuery = useGetContracts<Contract[]>({
    disablePagination: true,
    ownerCodes: [ownerCode],
  });

  const ticketTypesQuery = useGetHelpdeskTicketTypes();

  const setMessagesBasedOnTicketStep = (ticketStep: number) => {
    const currentStep = ticketCreationSteps.find(({ step }) => step === ticketStep);
    setMessages([
      ...messages,
      {
        type: MessageType.Request,
        content: currentStep.message,
      },
    ]);
  };

  const handleShowButtonsPanelButtonClick = () => {
    setIsButtonsPanelShown(!isButtonsPanelShown);
  };

  const handleSelectContractId = (contractId: number) => {
    setSelectedContractId(contractId);
    setMessages([...messages, { type: MessageType.Response, content: contractId.toString() }]);
    setTicketCreationStep(ticketCreationStep + 1);
    setMessagesBasedOnTicketStep(ticketCreationStep + 1);
  };

  const handleSelectTicketType = (ticketTypeId: number) => {
    setSelectedTicketTypeId(ticketTypeId);
    setMessages([
      ...messages,
      { type: MessageType.Response, content: ticketTypesQuery.data?.find((type) => type.id === ticketTypeId)?.name },
    ]);

    const selectedContract = contractsQuery.data?.find((contract) => contract.id === selectedContractId);
    const selectedTicketType = ticketTypesQuery.data?.find((ticketType) => ticketType.id === ticketTypeId);

    createHelpdeskTicket({
      name: `Contract ${selectedContract.contractNumber} - ${selectedTicketType.name}`,
      partnerName: accountName || 'Asad',
      xContractNumber: selectedContract.id,
      ticketTypeId: ticketTypeId,
    });
  };

  const renderContractButtons = () => (
    <div className="gap-2 grid grid-cols-2 w-full max-h-40 overflow-y-auto">
      {contractsQuery.data?.map((contract) => (
        <button
          onClick={() => handleSelectContractId(contract.id)}
          key={contract.contractNumber}
          className="flex justify-center items-center bg-[#F9FAE0] hover:bg-[#EBF163] rounded-[14px] h-12 basis-1/2"
        >
          <span className="font-medium text-[#595959] text-base">#{contract.contractNumber}</span>
        </button>
      ))}
    </div>
  );

  const renderTicketTypeButtons = () => (
    <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
      {ticketTypesQuery?.data?.map((ticketType) => (
        <button
          onClick={() => handleSelectTicketType(ticketType.id)}
          key={ticketType.id}
          className="flex justify-center items-center bg-[#F9FAE0] hover:bg-[#EBF163] px-8 rounded-[14px] h-12"
        >
          <span className="font-medium text-[#595959] text-base">{ticketType.displayName}</span>
        </button>
      ))}
    </div>
  );

  const ticketCreationSteps = [
    {
      step: 1,
      placeholder: 'Please choose contract number',
      message: 'Hello! You have contacted the support service.\nPlease select your contract number.',
      renderButtons: renderContractButtons,
    },
    {
      step: 2,
      placeholder: 'Select topic of your request',
      message: 'Select theme of request.',
      renderButtons: renderTicketTypeButtons,
    },
  ];

  const activeStep = ticketCreationSteps.find(({ step }) => step === ticketCreationStep);

  if (!contractsQuery.data) {
    return null;
  }

  return (
    <div className="flex flex-col flex-1 h-full md:h-auto">
      <div className="flex items-center gap-3 bg-white mb-5 px-6 py-4 rounded-[32px] h-[82px]">
        <button onClick={onBackButtonClick} className="text-gray-10">
          <ArrowLeft />
        </button>
        <img src={logo} className="w-12 h-12 object-contain" alt="logo" />
        <div className="flex flex-col flex-1 justify-between">
          <span className="font-medium text-2xl text-ellipsis text-gray-10 text-nowrap overflow-hidden">{t('createSupportRequest')}</span>
        </div>
      </div>
      <div className="flex flex-col flex-1 justify-between gap-6 bg-white p-6 rounded-t-[32px] overflow-y-auto">
        <div className="flex flex-col flex-grow flex-1 gap-4 overflow-y-auto">
          {messages.map((message, index) => (
            <div
              key={index}
              dangerouslySetInnerHTML={{ __html: message.content }}
              className={cn(
                'px-[15px] py-3 rounded-l-[16px] rounded-t-[16px] min-w-1/2 font-normal text-[#595959] text-sm',
                message.type === MessageType.Request ? 'bg-[#F5F5F5] self-start' : 'bg-[#EBF163] self-end',
              )}
            />
          ))}
        </div>
        <div className="flex items-start gap-2">
          <button className="flex items-center h-14 text-[#B9B9B9]">
            <Paperclip />
          </button>
          <div className="flex flex-col flex-1 gap-2">
            <div className="flex gap-2 h-14">
              <form className="flex flex-1 justify-between gap-4 border-[#DFDFDF] p-4 border rounded-[14px]">
                <input disabled placeholder={activeStep.placeholder} className="flex-1 w-full font-normal text-base outline-none" />
                <button onClick={handleShowButtonsPanelButtonClick} type="button" className="text-[#B9B9B9] hover:text-[#D5DC2E]">
                  <Grid01 />
                </button>
              </form>
            </div>
            {isButtonsPanelShown && activeStep.renderButtons()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTicketForm;
